import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Grid from '@mui/material/Unstable_Grid2';
import axios from '../../../services/ApiService';
import { TotalOverview } from '../../layout/overview/total-overview';
import { Traffic } from '../../layout/overview/traffic';
import { RecentWorkflows } from '../../layout/overview/recent-workflows';
import { WorkflowTasks, Assignment } from '../../types/ProgramTypes';

function AdminHome() {

  // State for business count
  const [businessCount, setBusinessCount] = useState('0');
  const [businessCountLoading, setBusinessCountLoading] = useState(true);

  // State for agent count
  const [agentCount, setAgentCount] = useState('0');
  const [agentCountLoading, setAgentCountLoading] = useState(true);
  
  // State for workflow count
  const [workflowCount, setWorkflowCount] = useState('0');
  const [workflowCountLoading, setWorkflowCountLoading] = useState(true);
  
  // State for task count
  const [taskCount, setTaskCount] = useState('0');
  const [taskCountLoading, setTaskCountLoading] = useState(true);

  // State for recent tasks
  const [recentTasks, setRecentTasks] = useState<WorkflowTasks[]>([]);
  const [recentTasksLoading, setRecentTasksLoading] = useState(true);
  
  // State for workflow statuses chart
  const [workflowStatuses, setWorkflowStatuses] = useState<number[]>([0, 0, 0]); // [completed, inProgress, pending]
  const [workflowStatusesLoading, setWorkflowStatusesLoading] = useState(true);

  useEffect(() => {
    // Fetch client count
    const fetchBusinessCount = async () => {
      try {
        const response = await axios.get('/getBusinessCount');
        setBusinessCount(response.data);
      } catch (error) {
        console.error('Error fetching business count.');
      } finally {
        setBusinessCountLoading(false);
      }
    };

    const fetchAgentCount = async () => {
      try {
        const response = await axios.get('/getAgentCount');
        setAgentCount(response.data);
      } catch (error) {
        console.error('Error fetching agent count.');
      } finally {
        setAgentCountLoading(false);
      }
    };

    // Fetch workflow count
    const fetchWorkflowCount = async () => {
      try {
        const response = await axios.get('/getWorkflowCount');
        setWorkflowCount(response.data);
      } catch (error) {
        console.error('Error fetching workflow count.');
      } finally {
        setWorkflowCountLoading(false);
      }
    };

    // Fetch task count
    const fetchTaskCount = async () => {
      try {
        const response = await axios.get('/getTaskCount');
        setTaskCount(response.data);
      } catch (error) {
        console.error('Error fetching task count.');
      } finally {
        setTaskCountLoading(false);
      }
    };

    // Fetch recent tasks
    const fetchRecentTasks = async () => {
      try {
        const response = await axios.get('/getTasks');
        setRecentTasks(response.data);
      } catch (error) {
        console.error('Error fetching recent tasks.');
      } finally {
        setRecentTasksLoading(false);
      }
    };
    
    // Fetch workflow statuses for chart
    const fetchWorkflowStatuses = async () => {
      try {
        const response = await axios.get('/getWorkflowStatuses');
        const assignments: Assignment[] = response.data;
        
        // Count statuses
        let completed = 0;
        let inProgress = 0;
        let pending = 0;
        
        assignments.forEach(assignment => {
          switch(assignment.status.toUpperCase()) {
            case 'COMPLETED':
              completed++;
              break;
            case 'ACTIVE':
              inProgress++;
              break;
            case 'PENDING':
              pending++;
              break;
            default:
              // Handle other statuses if needed
              break;
          }
        });
        
        // Store raw counts, percentages will be calculated in the Traffic component
        setWorkflowStatuses([completed, inProgress, pending]);
      } catch (error) {
        console.error('Error fetching workflow statuses.');
      } finally {
        setWorkflowStatusesLoading(false);
      }
    };

    // Call all fetch functions asynchronously
    fetchBusinessCount();
    fetchAgentCount();
    fetchWorkflowCount();
    fetchTaskCount();
    fetchRecentTasks();
    fetchWorkflowStatuses();
  }, []);
  
  return (
    <>
    <Helmet><title>BVT Admin - Home</title></Helmet>
    <div>
      <Grid container spacing={3} marginTop={1}>
          <Grid lg={3} sm={6} xs={12}>
            <TotalOverview 
              icon='user' 
              label='Businesses' 
              sx={{ height: '90%' }} 
              value={businessCount} 
              loading={businessCountLoading}
            />
          </Grid>
          <Grid lg={3} sm={6} xs={12}>
            <TotalOverview 
              icon='user' 
              label='Agents' 
              sx={{ height: '90%' }} 
              value={agentCount} 
              loading={agentCountLoading}
            />
          </Grid>
          <Grid lg={3} sm={6} xs={12}>
            <TotalOverview 
              icon='workflow' 
              label='Active Workflows' 
              sx={{ height: '90%' }} 
              value={workflowCount} 
              loading={workflowCountLoading}
            />
          </Grid>
          <Grid lg={3} sm={6} xs={12}>
            <TotalOverview 
              icon='tasks' 
              label='Pending Tasks' 
              sx={{ height: '90%' }} 
              value={taskCount} 
              loading={taskCountLoading}
            />
          </Grid>

          <Grid lg={8} xs={12}>
            <RecentWorkflows
              workflows={recentTasks}
              loading={recentTasksLoading}
              sx={{ height: '100%' }}
            />
          </Grid>
          <Grid lg={4} md={6} xs={12}>
            <Traffic 
              title='Workflow Performance' 
              chartSeries={workflowStatuses} 
              labels={['Completed', 'In Progress', 'Pending']} 
              sx={{ height: '100%' }} 
              loading={workflowStatusesLoading}
            />
          </Grid>
      </Grid>
    </div> 
    </>
  )
}

export default AdminHome;